import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CameraInitResult } from 'services/camera.service';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';

/**
 * The central area of a scanner page: the live camera preview while the
 * camera runs, and the captured-photo collage (with the analyzing
 * overlay on top) once the camera stops.
 *
 * The page keeps full control of the camera: the inner cs-camera-view
 * is exposed as the public `cameraView` field, so pages call
 * `stage.cameraView.capturePhoto()`, `.stopCamera()`, `.reinitCamera()`
 * etc. exactly as they did before the shared-component refactor.
 *
 * The collage photo elements are exposed for the shared capture
 * animations (photo flying between reel and collage).
 */
@Component({
  selector: 'cs-scanner-camera-stage',
  templateUrl: 'cs-scanner-camera-stage.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsScannerCameraStageComponent {
  /** Captured photos to show in the collage when the camera is off. */
  @Input() photos: { dataUrl: string }[] = [];
  /** Camera aspect ratio — a per-scanner choice (owner decision #4). */
  @Input() aspectRatio: '1:1' | '4:3' | 'auto' = 'auto';
  /** True while the AI scan runs — shows the analyzing overlay. */
  @Input() analyzing = false;
  /** Progress percentage for the analyzing overlay, 0-100. */
  @Input() progress = 0;
  /** Status line for the analyzing overlay, already translated. */
  @Input() statusText = '';
  /** Optional retry line for the analyzing overlay. */
  @Input() retryMessage: string | null = null;
  /** Bare mode (meal photo scanner): no card background or padding
   *  around the collage — the lone photo sits directly on the page
   *  (owner decision #6). */
  @Input() bareCollage = false;
  /** When true the stage does NOT render its own analyzing overlay — the page shows
   *  the progress elsewhere (the scanner pages render it in the bottom bar, where the
   *  shutter sits, so it never overlaps the photo). All scanner pages set this
   *  today; default false renders the in-collage overlay, preserved for any other
   *  consumer. */
  @Input() suppressAnalyzingOverlay = false;

  /** Tapping a collage photo while not analyzing — page removes it.
   *  No scanner binds this today (all use explicit Retake buttons /
   *  the reel's X instead), which makes the collage photos inert:
   *  clickability is gated on `remove.observed`. */
  @Output() remove = new EventEmitter<number>();

  /** True when the page listens for photo taps (drives the pointer
   *  cursor and the click handling). */
  get photosClickable(): boolean {
    return !this.analyzing && this.remove.observed;
  }
  // re-emitted camera events, so pages bind to the stage only
  @Output() cameraReady = new EventEmitter<void>();
  @Output() cameraError = new EventEmitter<CameraInitResult>();
  @Output() permissionChange = new EventEmitter<void>();

  /** The wrapped camera view — the page drives the camera through this. */
  @ViewChild('cameraView') cameraView: CsCameraViewComponent;

  @ViewChildren('collagePhoto') private collagePhotoRefs: QueryList<ElementRef<HTMLElement>>;

  /** Collage photo elements, for the shared capture animations. */
  get collagePhotoElements(): HTMLElement[] {
    return (this.collagePhotoRefs?.toArray() ?? []).map(ref => ref.nativeElement);
  }

  onPhotoClick(index: number) {
    if (!this.photosClickable) return;
    this.remove.emit(index);
  }
}
